import { html, TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';

export type ModalContent = 'calendar' | 'booking-request' | 'offer-request';
export type OfferRequestVariant = 'default' | 'extended' | 'costs' | 'extended-costs';
export type BookingRequestVariant = 'default' | 'costs';

export interface DialogBodyTemplateOptions {
    modalContent: ModalContent;
    bookingRequestVariant: BookingRequestVariant;
    offerRequestVariant: OfferRequestVariant;
    offerRequestDescriptionOptional: boolean;
    newsletter: string;
    enableRecap: boolean;
    identifier: string;
    productTitle: string;
    lang: string;
    brand: string;
    currency: string;
    tenant: string;
    componentTitle: string;
    fallbackPrice: string;
    durationString: string;
    roundtripTypes: string;
    roundtripId: string;
    roundtripKey: string;
    phoneNumber: string;
    bookingDayOffset: number;
    showOperator: boolean;
    /** OpenAPI `roundtripOffer.destination` → CRM `opportunity-destination` on embedded forms. */
    opportunityDestination: string;
    /** OpenAPI `roundtripOffer.startAndEndPoint` → offer panel `start-end-destination`. */
    startAndEndPoint: string;
    startPoint: string;
    languages: string;
    groupSize: string;
    bestTravelTime: string;
    bestOffer: Record<string, unknown> | null;
    additionalTrackingData: Record<string, unknown>;
    selectedOfferData: Record<string, unknown> | null;
    opportunityOwningTeam: string;
    opportunityProductBrand: string;
    opportunitySource: string;
    opportunityType: string;
    facetAudience: string;
    facetStyle: string;
    facetMotive: string;
}

export const renderDialogBody = (options: DialogBodyTemplateOptions): TemplateResult => {
    if (options.modalContent === 'booking-request') {
        return html`
            <der-tm-roundtrip-booking-request
                lang=${options.lang}
                brand=${options.brand}
                currency=${options.currency}
                tenant=${options.tenant}
                identifier=${options.identifier}
                variant=${options.bookingRequestVariant}
                newsletter=${options.newsletter}
                ?enable-recap=${options.enableRecap}
                product-key=${options.roundtripKey}
                trip-title=${options.productTitle || options.componentTitle}
                opportunity-title=${options.productTitle || options.componentTitle}
                opportunity-destination=${options.opportunityDestination}
                opportunity-product-id=${options.roundtripId || options.identifier}
                opportunity-product-brand=${options.opportunityProductBrand}
                opportunity-owning-team=${options.opportunityOwningTeam}
                opportunity-source=${options.opportunitySource || 'roundtrip-stage'}
                opportunity-type=${options.opportunityType}
                support-phone=${options.phoneNumber}
                facet_audience=${ifDefined(options.facetAudience)}
                facet_style=${ifDefined(options.facetStyle)}
                facet_motive=${ifDefined(options.facetMotive)}
                .offerData=${options.selectedOfferData ?? undefined}
                .additionalTrackingData=${options.additionalTrackingData}
            ></der-tm-roundtrip-booking-request>
        `;
    }

    if (options.modalContent === 'offer-request') {
        return html`
            <der-tm-offer-request
                lang=${options.lang}
                brand=${options.brand}
                currency=${options.currency}
                tenant=${options.tenant}
                form-name="offerForm"
                variant=${options.offerRequestVariant}
                newsletter=${options.newsletter}
                ?enable-recap=${options.enableRecap}
                ?description-optional=${options.offerRequestDescriptionOptional}
                show-header
                contact-language="948150000"
                opportunity-title=${options.productTitle || options.componentTitle}
                opportunity-destination=${options.opportunityDestination}
                opportunity-product-id=${options.roundtripId || options.identifier}
                opportunity-owning-team=${options.opportunityOwningTeam}
                opportunity-source=${options.opportunitySource}
                opportunity-type=${options.opportunityType}
                facet_audience=${ifDefined(options.facetAudience)}
                facet_style=${ifDefined(options.facetStyle)}
                facet_motive=${ifDefined(options.facetMotive)}
                .additionalTrackingData=${options.additionalTrackingData}
            ></der-tm-offer-request>
        `;
    }

    return html`
        <der-tm-roundtrip-offer-panel
            variant="calendar"
            identifier=${options.identifier}
            lang=${options.lang}
            brand=${options.brand}
            currency=${options.currency}
            tenant=${options.tenant}
            component-title=${options.componentTitle}
            fallback-price=${options.fallbackPrice}
            duration-string=${options.durationString}
            roundtrip-types=${options.roundtripTypes}
            roundtrip-id=${options.roundtripId}
            roundtrip-key=${options.roundtripKey}
            phone-number=${options.phoneNumber}
            booking-day-offset=${options.bookingDayOffset}
            ?show-operator=${options.showOperator}
            start-end-destination=${options.startAndEndPoint}
            start-point=${options.startPoint}
            languages=${options.languages}
            group-size=${options.groupSize}
            best-travel-time=${options.bestTravelTime}
            facet_audience=${ifDefined(options.facetAudience)}
            facet_style=${ifDefined(options.facetStyle)}
            facet_motive=${ifDefined(options.facetMotive)}
            .bestOffer=${options.bestOffer}
            .additionalTrackingData=${options.additionalTrackingData}
        ></der-tm-roundtrip-offer-panel>
    `;
};
